import { createTheme, Theme, ThemeProvider } from "@mui/material/styles";
import React, { ReactNode, useContext } from "react";

/**
 * The height of the app bar. Whatever starts under the bar leaves this much
 * room for it.
 */
const APP_BAR_HEIGHT = 56;

export function getTheme(): Theme {
  return createTheme({
    palette: {
      primary: { main: "#081a38" },
      secondary: { main: "#ffe4c4" },
      background: {
        default: "#f4f4f4",
        paper: "#fafafa",
      },
      data: {
        positive: {
          main: "#38925E",
          light: "#70B58B",
          dark: "#2E7D6D",
          contrastText: "#FFFFFF",
        },
        negative: {
          main: "#C94C4C",
          light: "#E18282",
          dark: "#8B3A4A",
          contrastText: "#FFFFFF",
        },
      },
    },
    mixins: { toolbar: { minHeight: APP_BAR_HEIGHT } },
    typography: {
      fontFamily: "Inter",
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 400,
        md: 800,
        lg: 1200,
        xl: 1536,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
              borderRadius: 8,
              backgroundColor: "rgba(0,0,0,.1)",
              minHeight: 24,
            },
          },
        },
      },
    },
  });
}

const theme = getTheme();

// the app has one theme, so the context needs no provider: its default is it
const ThemeContext = React.createContext({ theme });

export const ThemeContextProvider = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}) => (
  <>
    {/* the browser paints its own bar above the page in this colour, the
        status bar of the installed app for one: it continues the app bar.
        React moves the tag into the head, where the first tag of this name
        counts, so index.html must not have one. */}
    <meta name="theme-color" content={theme.palette.primary.main} />
    <ThemeProvider theme={theme}>{children}</ThemeProvider>
  </>
);

export const useTheme = () => useContext(ThemeContext);
